import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export const SEGMENT_COLORS = {
  customer: '#2563eb',
  cspire: '#7c3aed',
  franklin: '#059669',
};

export const STATUS_COLORS = {
  assigned: '#2563eb',
  scheduled: '#0891b2',
  in_progress: '#9333ea',
  queued: '#d97706',
  unassigned: '#dc2626',
  none: '#6b7280',
  completed: '#16a34a',
  return: '#ea580c',
};

const TECH_COLORS = [
  '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#c2410c', '#4f46e5'
];

export const getTechColor = (technicianId, techColorMap = {}) => {
  if (!technicianId) return STATUS_COLORS.none;
  if (techColorMap[technicianId]) return techColorMap[technicianId];
  const hash = technicianId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return TECH_COLORS[hash % TECH_COLORS.length];
};

const createMarkerIcon = (color, label = '') => {
  const display = label ? String(label).slice(0, 2).toUpperCase() : '•';
  return L.divIcon({
    className: 'service-map-marker',
    html: `<div style="background-color:${color};color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid white;">${display}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

const createTechIcon = (initials, color) => L.divIcon({
  className: 'tech-location-marker',
  html: `<div style="background-color:${color};color:white;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;box-shadow:0 3px 8px rgba(0,0,0,0.4);border:3px solid white;">${initials}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

const formatStatus = (status) => (status || 'none').replace(/_/g, ' ');

const ServiceMap = ({
  points = [],
  technicians = [],
  showTechnicians = false,
  colorMode = 'segment',
  techColorMap = {},
  routeGeometry = null,
  historyPath = [],
  onPointClick,
  height = 'calc(100vh - 12rem)',
  className = '',
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const techLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const historyLayerRef = useRef(null);

  useEffect(() => {
    if (!mapInstanceRef.current && mapRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([29.7604, -95.3698], 10);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);

      clusterGroupRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
      });
      mapInstanceRef.current.addLayer(clusterGroupRef.current);

      techLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      routeLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      historyLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        clusterGroupRef.current = null;
        techLayerRef.current = null;
        routeLayerRef.current = null;
        historyLayerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !clusterGroupRef.current) return;

    clusterGroupRef.current.clearLayers();
    const bounds = L.latLngBounds([]);

    points.forEach((point) => {
      if (point.lat == null || point.lng == null) return;

      const color = colorMode === 'status'
        ? (STATUS_COLORS[point.status] || STATUS_COLORS.none)
        : colorMode === 'technician'
          ? getTechColor(point.technicianId, techColorMap)
          : (SEGMENT_COLORS[point.entityType] || SEGMENT_COLORS.customer);

      const marker = L.marker([point.lat, point.lng], {
        icon: createMarkerIcon(color, point.entityType?.[0]?.toUpperCase()),
      });

      const popupHtml = `
        <div style="padding:8px;min-width:220px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};"></span>
            <strong style="font-size:14px;">${point.label || 'Unknown'}</strong>
          </div>
          ${point.subLabel ? `<p style="font-size:12px;color:#6b7280;margin:0 0 4px;">${point.subLabel}</p>` : ''}
          <p style="font-size:11px;color:#9ca3af;margin:0 0 4px;text-transform:capitalize;">${point.entityType}</p>
          <p style="font-size:12px;margin:0 0 2px;">Status: <strong>${formatStatus(point.status)}</strong></p>
          ${point.isDue ? `<p style="font-size:12px;color:#dc2626;margin:0 0 2px;">Due: ${point.dueReason || 'Yes'}</p>` : ''}
          ${point.technicianName ? `<p style="font-size:12px;margin:0 0 8px;">Tech: ${point.technicianName}</p>` : ''}
          ${onPointClick ? `<button data-point-id="${point.id}" data-entity-type="${point.entityType}" class="service-map-detail-btn" style="margin-top:4px;padding:4px 10px;background:#2563eb;color:white;font-size:12px;border-radius:4px;border:none;cursor:pointer;">View Details</button>` : ''}
        </div>
      `;

      marker.bindPopup(popupHtml);
      clusterGroupRef.current.addLayer(marker);
      bounds.extend([point.lat, point.lng]);
    });

    if (points.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [points, colorMode, techColorMap, onPointClick]);

  useEffect(() => {
    if (!techLayerRef.current) return;
    techLayerRef.current.clearLayers();
    if (!showTechnicians) return;

    technicians.forEach((tech) => {
      if (!tech.location?.lat || !tech.location?.lng) return;
      const initials = `${tech.firstName?.[0] || ''}${tech.lastName?.[0] || ''}`.toUpperCase();
      const color = getTechColor(tech.id, techColorMap);
      const marker = L.marker([tech.location.lat, tech.location.lng], {
        icon: createTechIcon(initials, color),
      });
      const recordedAt = tech.location.recordedAt
        ? new Date(tech.location.recordedAt).toLocaleString()
        : 'Unknown';
      marker.bindPopup(`
        <div style="padding:8px;">
          <strong>${tech.firstName} ${tech.lastName}</strong>
          <p style="font-size:12px;color:#6b7280;margin:4px 0 0;">Last updated: ${recordedAt}</p>
        </div>
      `);
      techLayerRef.current.addLayer(marker);
    });
  }, [technicians, showTechnicians, techColorMap]);

  useEffect(() => {
    if (!routeLayerRef.current) return;
    routeLayerRef.current.clearLayers();
    if (!routeGeometry?.coordinates?.length) return;

    const latLngs = routeGeometry.coordinates.map(([lng, lat]) => [lat, lng]);
    L.polyline(latLngs, { color: '#2563eb', weight: 4, opacity: 0.8 }).addTo(routeLayerRef.current);
  }, [routeGeometry]);

  useEffect(() => {
    if (!historyLayerRef.current) return;
    historyLayerRef.current.clearLayers();
    if (!historyPath?.length) return;

    const latLngs = historyPath.map((p) => [p.lat, p.lng]);
    L.polyline(latLngs, { color: '#9333ea', weight: 3, opacity: 0.7, dashArray: '6 4' }).addTo(historyLayerRef.current);
    if (latLngs.length > 0) {
      L.circleMarker(latLngs[0], { radius: 6, color: '#16a34a', fillColor: '#16a34a', fillOpacity: 1 })
        .addTo(historyLayerRef.current);
      L.circleMarker(latLngs[latLngs.length - 1], { radius: 6, color: '#dc2626', fillColor: '#dc2626', fillOpacity: 1 })
        .addTo(historyLayerRef.current);
    }
  }, [historyPath]);

  useEffect(() => {
    const handleDetailClick = (event) => {
      const btn = event.target.closest('.service-map-detail-btn');
      if (!btn || !onPointClick) return;
      const point = points.find(
        (p) => p.id === btn.dataset.pointId && p.entityType === btn.dataset.entityType
      );
      if (point) onPointClick(point);
    };

    const container = mapRef.current;
    container?.addEventListener('click', handleDetailClick);
    return () => container?.removeEventListener('click', handleDetailClick);
  }, [points, onPointClick]);

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full rounded-lg border border-gray-300" style={{ height }} />
      {points.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 rounded-lg z-[999] pointer-events-none">
          <p className="text-gray-500 text-sm">No locations match the current filters</p>
        </div>
      )}
    </div>
  );
};

export default ServiceMap;
